import React, { useState, useEffect, useRef } from "react";

// ── Types pour l'API Kayak ──────────────────────────────────────────────────
export interface KayakLocation {
  id: string;
  objectID?: string;
  kayakId?: string;
  displayname: string;
  loctype: string;
  name: string;
  cityname: string;
  country: string;
  ap: string;
  apicode: string;
  isMetroOnly?: boolean;
  shortdisplayname?: string;
  searchFormPrimary?: string;
  searchFormSecondary?: string;
  secondary?: string;
  displayType?: {
    type: string;
    displayName: string;
  };
}

// ── Composant AutoComplete Kayak ───────────────────────────────────────────
// Extracted from the flight ticket request page so any form needing an
// airport/city picker (flights, dummy tickets, ...) can reuse the same
// input + dropdown instead of duplicating the fetch/debounce logic.
const KayakFlightSearchInput = ({
  label,
  placeholder,
  value,
  onChange,
  required = true,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (val: string) => void;
  required?: boolean;
}) => {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<KayakLocation[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `https://booking.kayak.com/mvm/smartyv2/search?f=j&s=flight&where=${encodeURIComponent(query)}`
        );
        const data = await response.json();
        setResults(Array.isArray(data) ? data : []);
        setIsOpen(true);
      } catch (error) {
        console.error("Erreur lors de la recherche des vols Kayak:", error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (item: KayakLocation) => {
    const selectedText = item.shortdisplayname || item.displayname || `${item.name} (${item.apicode})`;
    setQuery(selectedText);
    onChange(selectedText);
    setIsOpen(false);
  };

  return (
    <div className="rq-field" ref={wrapperRef}>
      <label className="rq-field__label">
        {label} {required && <span>*</span>}
      </label>
      <input
        className="rq-field__input"
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
        }}
        onFocus={() => {
          if (results.length > 0) setIsOpen(true);
        }}
      />

      {isOpen && results.length > 0 && (
        <div className="kayak-dropdown">
          {results.map((item, index) => {
            const isCityHeader = item.loctype === "ct" || item.isMetroOnly || item.displayType?.type === "city";
            const isSubAirport = item.loctype === "ap" && !isCityHeader;

            return (
              <div
                key={item.id || item.objectID || index}
                className={`kayak-dropdown-item ${isSubAirport ? "kayak-sub-item" : ""}`}
                onClick={() => handleSelect(item)}
              >
                {isSubAirport && (
                  <svg className="kayak-arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                )}

                <div className="kayak-icon-box">
                  <svg viewBox="0 0 24 24">
                    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
                  </svg>
                </div>

                <div className="kayak-info">
                  <div className="kayak-title-row">
                    <span className="kayak-main-title">
                      {item.name || item.cityname}
                    </span>
                    <span className="kayak-iata-code">
                      {item.apicode || item.ap || (item.kayakId !== item.cityname ? item.kayakId : "")}
                    </span>
                  </div>
                  <div className="kayak-subtitle">
                    {isCityHeader ? (item.secondary || "All airports") : (item.cityname || item.country)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KayakFlightSearchInput;

// ── CSS required by this component ─────────────────────────────────────────
// The .rq-field / .kayak-* class names come from Ticket.tsx's <PageStyles />.
// If you use this component on a page that doesn't already import those
// styles (like DummyTicket.tsx), add this block once anywhere on the page:
export const KayakInputStyles = () => (
  <style>{`
    .rq-field { display: flex; flex-direction: column; gap: 6px; position: relative; }
    .rq-field__label { font-size: 11px; font-weight: 600; color: var(--teal, #294b5a); }
    .rq-field__label span { color: var(--gold, #d3be76); margin-left: 2px; }
    .rq-field__input {
      font-size: 14px; color: var(--ink, #1a2f38); background: var(--cream, #f7f4ee);
      border: 1.5px solid var(--mist, #ede9e0); border-radius: var(--radius-sm, 10px);
      padding: 12px 16px; outline: none; transition: border-color .2s, box-shadow .2s;
      width: 100%; font-family: 'DM Sans', sans-serif; box-sizing: border-box;
    }
    .rq-field__input::placeholder { color: #b0ab9f; font-weight: 400; }
    .rq-field__input:focus { border-color: var(--gold-d, #b09a50); box-shadow: 0 0 0 3px rgba(211,190,118,.15); background: #fff; }

    .kayak-dropdown {
      position: absolute; top: 100%; left: 0; right: 0;
      background: #ffffff; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,.15);
      border: 1px solid #e0e0e0; z-index: 1000; max-height: 320px; overflow-y: auto;
      margin-top: 4px; padding: 8px 0;
    }
    .kayak-dropdown-item { display: flex; align-items: center; padding: 10px 16px; cursor: pointer; transition: background-color .15s ease; }
    .kayak-dropdown-item:hover { background-color: #f4f6f8; }
    .kayak-sub-item { padding-left: 28px; }
    .kayak-arrow-icon { width: 14px; height: 14px; margin-right: 8px; color: #1a1a1a; flex-shrink: 0; }
    .kayak-icon-box { width: 36px; height: 36px; background-color: #f0f2f5; border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-right: 12px; flex-shrink: 0; }
    .kayak-icon-box svg { width: 18px; height: 18px; fill: #4a5568; }
    .kayak-info { display: flex; flex-direction: column; }
    .kayak-title-row { display: flex; align-items: center; gap: 6px; }
    .kayak-main-title { font-size: 14px; font-weight: 700; color: #1a1a1a; }
    .kayak-iata-code { font-size: 12px; font-weight: 600; color: #8c9ba5; }
    .kayak-subtitle { font-size: 12px; color: #6b7280; margin-top: 2px; }
  `}</style>
);